import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  API_BASE_URL, VISA_CAREER_TABS, VISA_CAREER_COLUMNS,
  VISA_CAREER_MESSAGES, getSubmitButtonLabel,
  getContentUpdateDescription, getSheetUrl,
  VISA_CAREER_DUPLICATE_CHECK, findDuplicates, formatDateOnly,
  VISA_CAREER_ICON_PRESETS, VISA_CAREER_EMAIL_DEFAULTS,
  ROLE_OVERVIEW_LABEL_OPTIONS,
} from "./constants/adminConstants";
import { resolveIcon } from "../Main/Footer/FooterData";
import { compressImageFile, validateImageFile } from "./compressImage";
import { uploadToCloudinary } from "./uploadImage";
import "./css/adminStyles.css";

// ── Scrollable table wrapper with left/right scroll buttons ────────────

function TableWithScrollButtons({ children, style }) {
  const scrollRef = useRef(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (el) {
      setShowLeft(el.scrollLeft > 5);
      setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 5);
    }
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);
    const ro = new ResizeObserver(checkScroll);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
      ro.disconnect();
    };
  }, [checkScroll]);

  const scrollTo = (dir) => {
    const el = scrollRef.current;
    if (el) el.scrollBy({ left: dir === "left" ? -300 : 300, behavior: "smooth" });
  };

  return (
    <div className="AdminTableScrollWrap" style={style}>
      <div className="AdminScrollBar">
          <button className="AdminScrollBtn" onClick={() => scrollTo("left")} disabled={!showLeft} title="Scroll left">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="10 4 6 8 10 12" />
            </svg>
          </button>
          <button className="AdminScrollBtn" onClick={() => scrollTo("right")} disabled={!showRight} title="Scroll right">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 4 10 8 6 12" />
            </svg>
          </button>
        </div>
      <div ref={scrollRef} className="AdminTableScroll">
        {children}
      </div>
    </div>
  );
}

function AdminVisaCareerEditor({ user, section, onBack }) {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [activeTab, setActiveTab] = useState("departments");
  const [sectionInfo, setSectionInfo] = useState(null);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);
  const [successTitle, setSuccessTitle] = useState("");
  const [arraySearch, setArraySearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [filterSearch, setFilterSearch] = useState("");
  const [filterSearchOpen, setFilterSearchOpen] = useState(false);
  const filterSearchRef = useRef(null);
  useEffect(() => {
    if (filterSearchOpen && filterSearchRef.current) {
      filterSearchRef.current.focus();
    }
  }, [filterSearchOpen]);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState([]);
  const [duplicateActionLoading, setDuplicateActionLoading] = useState(false);
  const [pendingIconUpload, setPendingIconUpload] = useState(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  // Job listing form modal state
  const [showJobForm, setShowJobForm] = useState(false);
  const [editJobIndex, setEditJobIndex] = useState(null);
  const [editJobItem, setEditJobItem] = useState({});
  const [jobSortOrder, setJobSortOrder] = useState("oldest");
  const searchRef = useRef(null);
  const snapshotRef = useRef(null);
  const dragRef = useRef(null);
  const fileInputRef = useRef(null);
  const tabBarRef = useRef(null);
  useEffect(() => {
    const el = tabBarRef.current;
    if (!el) return;
    const wheelHandler = (e) => {
      if (e.deltaY !== 0) { e.preventDefault(); el.scrollLeft += e.deltaY; }
    };
    el.addEventListener("wheel", wheelHandler, { passive: false });
    return () => el.removeEventListener("wheel", wheelHandler);
  }, []);
  const tabDragRef = useRef(null);

  useEffect(() => {
    if (searchOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [searchOpen]);

  useEffect(() => {
    setArraySearch("");
    setSearchOpen(false);
    setFilterSearch("");
    setFilterSearchOpen(false);
  }, [activeTab]);

  // ── Generate a Job ID in TVC+deptLetter+number format ──
  // Example: TVCD001, TVCE002, TVCG003
  const getDeptPrefix = (department, deptList) => {
    if (!department) return "General";
    const list = deptList || content?.departments || [];
    const dept = list.find((d) => d.id === department);
    if (dept && dept.label) {
      return dept.label.replace(/[\s-]+/g, "");
    }
    return department.charAt(0).toUpperCase() + department.slice(1).replace(/[\s-]+/g, "");
  };

  const generateNextJobId = (department) => {
    const existing = content?.jobListings || [];
    const prefix = `TVC${getDeptPrefix(department)}`;
    let maxNum = 0;
    for (const item of existing) {
      const id = String(item.id || "");
      const match = id.match(new RegExp(`^${prefix}(\\d+)$`));
      if (match) {
        const num = parseInt(match[1], 10);
        if (num > maxNum) maxNum = num;
      }
    }
    const next = maxNum + 1;
    return prefix + String(next).padStart(3, "0");
  };

  // ── Normalise existing Job IDs to new format ──────────
  // TVCd001 → TVCData001, D001 → TVCData001, gd001 → TVCGuestDelight001
  const normalizeJobId = (id, department, deptList) => {
    if (!id) return "";
    const deptPrefix = getDeptPrefix(department, deptList);
    const pad = (n) => String(n).padStart(3, "0");
    const fullPrefix = `TVC${deptPrefix}`;
    if (new RegExp(`^${fullPrefix}\\d+$`).test(id)) return id;
    const m = id.match(/(\d+)$/);
    if (m) return `${fullPrefix}${pad(m[1])}`;
    return `${fullPrefix}001`;
  };

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    const token = sessionStorage.getItem("tvc_token");
    try {
      const res = await fetch(`${API_BASE_URL}/content/${section.key}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data && data.section) {
        const raw = data.section.content;
        // Merge emailConfig defaults so fields are never empty
        const merged = {
          ...raw,
          emailConfig: { ...VISA_CAREER_EMAIL_DEFAULTS, ...(raw.emailConfig || {}) },
          // Remove "all" department (frontend filter-only entry)
          departments: (raw.departments || []).filter((d) => String(d.id).trim() !== "all"),
          // Migrate old-format Job IDs to new full-name format
          jobListings: (raw.jobListings || []).map((item) => ({
            ...item,
            id: normalizeJobId(item.id, item.department, raw.departments),
          })),
        };
        setContent(merged);
        snapshotRef.current = JSON.stringify(merged);
        setSectionInfo({ version: data.section.version, updatedAt: data.section.updatedAt });
      } else {
        setMessage(VISA_CAREER_MESSAGES.apiError);
      }
    } catch (err) {
      setMessage(VISA_CAREER_MESSAGES.connectionError);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenSheet = () => {
    window.open(getSheetUrl(section.key), "_blank", "noopener,noreferrer");
  };

  const handlePushToSheet = async () => {
    const token = sessionStorage.getItem("tvc_token");
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/content/${section.key}/push-sheet`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ content }),
      });
      if (!res.ok) {
        const text = await res.text();
        setMessage(`Push failed (${res.status}): ${text.substring(0, 200)}`);
        return;
      }
      const data = await res.json();
      setMessage(data.success ? "Pushed to Google Sheet successfully!" : data.error || "Push failed.");
    } catch (err) {
      setMessage("Push error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePullFromSheet = async () => {
    const token = sessionStorage.getItem("tvc_token");
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/content/${section.key}/pull-sheet`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        const pulled = data.content;
        if (pulled.departments) {
          pulled.departments = pulled.departments.filter((d) => String(d.id).trim() !== "all");
        }
        setContent(pulled);
        setMessage("Pulled from Google Sheet. Review changes and click Publish to go live.");
      } else {
        setMessage(data.error || "Pull failed.");
      }
    } catch (err) {
      setMessage("Pull error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleArrayChange = (field, index, key, value) => {
    setContent((prev) => {
      const updated = { ...prev };
      const arr = [...(updated[field] || [])];
      arr[index] = { ...(arr[index] || {}), [key]: value };
      updated[field] = arr;
      return updated;
    });
  };

  const handleAddRowBefore = (field, index) => {
    setContent((prev) => {
      const updated = { ...prev };
      const arr = [...(updated[field] || [])];
      const emptyRow = {};
      const columns = VISA_CAREER_COLUMNS[activeTab] || [];
      for (const col of columns) {
        emptyRow[col.key] = "";
      }
      arr.splice(index, 0, emptyRow);
      updated[field] = arr;
      return updated;
    });
  };

  const handleAddRowAfter = (field, index) => {
    setContent((prev) => {
      const updated = { ...prev };
      const arr = [...(updated[field] || [])];
      const emptyRow = {};
      const columns = VISA_CAREER_COLUMNS[activeTab] || [];
      for (const col of columns) {
        emptyRow[col.key] = "";
      }
      arr.splice(index + 1, 0, emptyRow);
      updated[field] = arr;
      return updated;
    });
  };

  const handleDeleteRow = (field, index) => {
    setContent((prev) => {
      const updated = { ...prev };
      const arr = updated[field] || [];
      // When deleting a department, also remove its job listings
      if (field === "departments") {
        const deptId = arr[index]?.id;
        if (deptId && updated.jobListings) {
          updated.jobListings = updated.jobListings.filter((j) => j.department !== deptId);
        }
      }
      updated[field] = arr.filter((_, i) => i !== index);
      return updated;
    });
  };

  const handleMoveRowUp = (field, index) => {
    if (index === 0) return;
    setContent((prev) => {
      const updated = { ...prev };
      const arr = [...(updated[field] || [])];
      [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
      updated[field] = arr;
      return updated;
    });
  };

  const handleMoveRowDown = (field, index) => {
    setContent((prev) => {
      const updated = { ...prev };
      const arr = [...(updated[field] || [])];
      if (index >= arr.length - 1) return prev;
      [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]];
      updated[field] = arr;
      return updated;
    });
  };

  const handleIconUploadClick = (field, index, key) => {
    setPendingIconUpload({ field, index, key });
    fileInputRef.current?.click();
  };

  const handleIconFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file || !pendingIconUpload) return;
    const check = validateImageFile(file);
    if (!check.valid) { alert(check.message); e.target.value = ""; return; }
    compressImageFile(file, 48, 0.8).then(async (dataUrl) => {
      try {
        const url = await uploadToCloudinary(dataUrl, file.name);
        handleArrayChange(pendingIconUpload.field, pendingIconUpload.index, pendingIconUpload.key, url);
      } catch (err) {
        alert("Upload failed: " + err.message);
      }
      setPendingIconUpload(null);
    });
    e.target.value = "";
  };

  const handleIconPresetSelect = (field, index, key, kindKey, preset) => {
    setContent((prev) => {
      const updated = { ...prev };
      const arr = [...updated[field]];
      arr[index] = { ...arr[index], [key]: preset, [kindKey]: preset };
      updated[field] = arr;
      return updated;
    });
  };

  const handleOpenAddJob = (prefillDept) => {
    const columns = VISA_CAREER_COLUMNS.jobListings;
    const emptyRow = {};
    for (const col of columns) {
      emptyRow[col.key] = "";
    }
    if (prefillDept) {
      emptyRow.department = prefillDept;
      const dept = (content?.departments || []).find((d) => d.id === prefillDept);
      if (dept && dept.label) {
        emptyRow.departmentLabel = dept.label;
      }
    }
    // Always auto-generate the Job ID so it's never empty
    emptyRow.id = generateNextJobId(prefillDept || emptyRow.department);
    setEditJobIndex(null);
    setEditJobItem(emptyRow);
    setShowJobForm(true);
  };

  const handleOpenEditJob = (index) => {
    const item = content?.jobListings?.[index];
    if (!item) return;
    const filled = { ...item };
    // Ensure all column keys exist so form fields aren't empty
    for (const col of VISA_CAREER_COLUMNS.jobListings) {
      if (filled[col.key] === undefined) filled[col.key] = "";
    }
    // Auto-generate Job ID if missing
    if (!filled.id || !filled.id.trim()) {
      filled.id = generateNextJobId(filled.department);
    }
    // Auto-fill departmentLabel from departments list if empty
    if (!filled.departmentLabel && filled.department) {
      const dept = (content?.departments || []).find((d) => d.id === filled.department);
      if (dept && dept.label) {
        filled.departmentLabel = dept.label;
      }
    }
    setEditJobIndex(index);
    setEditJobItem(filled);
    setShowJobForm(true);
  };

  const handleCloseJobForm = () => {
    setShowJobForm(false);
    setEditJobIndex(null);
    setEditJobItem({});
  };

  const handleJobFieldChange = (key, value) => {
    setEditJobItem((prev) => {
      const updated = { ...prev, [key]: value };
      // Auto-generate Job ID when department changes (new or existing)
      if (key === "department") {
        const oldId = prev.id || "";
        if (!oldId || /^TVC[A-Z]\d+$/.test(oldId) || /^\d+$/.test(oldId)) {
          updated.id = generateNextJobId(value);
        }
      }
      // Auto-fill departmentLabel when department is selected
      if (key === "department" && value) {
        const dept = (content?.departments || []).find((d) => d.id === value);
        if (dept && dept.label) {
          updated.departmentLabel = dept.label;
        }
      }
      return updated;
    });
  };

  const handleSaveJobForm = () => {
    setContent((prev) => {
      const updated = { ...prev };
      const arr = [...(updated.jobListings || [])];
      if (editJobIndex === null) {
        const itemToAdd = { ...editJobItem };
        // Ensure the ID is set (auto-generate if still empty)
        if (!itemToAdd.id || !itemToAdd.id.trim()) {
          itemToAdd.id = generateNextJobId(itemToAdd.department);
        }
        arr.push(itemToAdd);
      } else {
        arr[editJobIndex] = { ...editJobItem };
      }
      updated.jobListings = arr;
      return updated;
    });
    handleCloseJobForm();
  };

  const handleSubmit = async () => {
    const dupes = findDuplicates(content, VISA_CAREER_DUPLICATE_CHECK);
    if (dupes.length > 0) {
      setDuplicateInfo(dupes);
      setShowDuplicateModal(true);
      return;
    }
    const role = user?.role || "Staff";
    if (role !== "Admin") {
      setNoteText("");
      setShowNoteModal(true);
      return;
    }
    await doSubmit();
  };

  const handleConfirmSubmit = async () => {
    if (!noteText.trim()) return;
    setShowNoteModal(false);
    await doSubmit(noteText.trim());
  };

  const handleContinueAnyway = async () => {
    setShowDuplicateModal(false);
    const role = user?.role || "Staff";
    if (role !== "Admin") {
      setNoteText("");
      setShowNoteModal(true);
      return;
    }
    await doSubmit();
  };

  const handleRemoveDuplicates = async () => {
    setDuplicateActionLoading(true);
    const cleanContent = JSON.parse(JSON.stringify(content));
    const sorted = [...duplicateInfo].sort((a, b) => b.duplicateIndex - a.duplicateIndex);
    for (const dup of sorted) {
      const arr = [...(cleanContent[dup.field] || [])];
      if (arr[dup.duplicateIndex]) {
        arr.splice(dup.duplicateIndex, 1);
        cleanContent[dup.field] = arr;
      }
    }
    setContent(cleanContent);
    setShowDuplicateModal(false);
    setDuplicateActionLoading(false);
    snapshotRef.current = JSON.stringify(cleanContent);
    const role = user?.role || "Staff";
    if (role !== "Admin") {
      setNoteText("");
      setShowNoteModal(true);
      return;
    }
    await doSubmit("", cleanContent);
  };

  const doSubmit = async (submitterNote, overrideContent) => {
    setMessage("");
    setSaving(true);
    const token = sessionStorage.getItem("tvc_token");
    const submitPayload = overrideContent || { ...content };
    const estimateSize = new Blob([JSON.stringify(submitPayload)]).size;
    if (estimateSize > 4 * 1024 * 1024) {
      setMessage(`Payload too large (${(estimateSize / 1024 / 1024).toFixed(1)} MB). Reduce image sizes before submitting.`);
      setSaving(false);
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/content/submit-change`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          description: getContentUpdateDescription(section.label),
          changes: {
            type: "content_update",
            sectionKey: section.key,
            content: submitPayload,
            ...(submitterNote ? { submitterNote } : {}),
          },
          sectionKey: section.key,
        }),
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { data = { error: text }; }
      if (res.ok) {
        const role = user?.role || "Staff";
        setSuccessTitle(role === "Admin" ? "Changes Published" : "Request made");
        setShowSuccessOverlay(true);
        snapshotRef.current = JSON.stringify(submitPayload);
        setSectionInfo((prev) => ({
          ...prev,
          version: (prev?.version || 0) + 1,
          updatedAt: new Date().toISOString(),
        }));
        // Auto-push to Google Sheet after successful publish
        try {
          await fetch(`${API_BASE_URL}/content/${section.key}/push-sheet`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ content: submitPayload }),
          });
        } catch {
          // Sheet push is secondary — silently ignore
        }
      } else {
        setMessage(data.error || `Server error (${res.status})`);
      }
    } catch (err) {
      setMessage(`Network error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const renderArrayEditor = (field, columns, opts) => {
    const useModal = opts?.modal || false;
    const items = content?.[field] || [];

    // Helper to display a cell value in read-only mode
    const displayValue = (item, col) => {
      const val = item[col.key];
      if (!val || val === "") return "—";
      if (col.key === "postedDate") return formatDateOnly(val);
      if (val.length > 60) return val.substring(0, 57) + "...";
      return val;
    };

    return (
      <div className="AdminCardMb">
        <div className="AdminSectionHeader">
          <h4 className="AdminSectionTitle">
            {field.replace(/([A-Z])/g, " $1").trim()}
          </h4>
          <div className="AdminFlexRowGap8 AdminFlexShrink0">
            <div className={`AdminPillSearchWrap ${searchOpen ? 'is-open' : ''}`}>
              <span className="AdminPillSearchIcon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
              </span>
              <input
                ref={searchRef}
                type="text"
                className="AdminPillSearch"
                placeholder="Search..."
                value={arraySearch}
                onChange={(e) => setArraySearch(e.target.value)}
                onBlur={() => setTimeout(() => setSearchOpen(false), 200)}
              />
              {!searchOpen && (
                <button className="AdminPillSearchOverlay" onClick={() => setSearchOpen(true)} type="button" aria-label="Search" />
              )}
            </div>
            <button
              className="AdminButton AdminButtonSmall AdminButtonSecondary AdminAddRowBtn"
              onClick={() => useModal ? handleOpenAddJob() : handleAddRowAfter(field, items.length - 1)}
            >
              + Add Row
            </button>
          </div>
        </div>
        <TableWithScrollButtons>
          <table className="AdminTable AdminTableEditor">
            <thead>
              <tr>
                <th className="AdminThOrder">Order</th>
                {columns.map((col) => (
                  <th key={col.key}>{col.label}</th>
                ))}
                <th className="AdminThActions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items
                .map((item, idx) => ({ item, idx }))
                .filter(({ item }) => {
                  if (!arraySearch.trim()) return true;
                  return columns.some((col) =>
                    String(item[col.key] || "").toLowerCase().includes(arraySearch.trim().toLowerCase())
                  );
                })
                .map(({ item, idx }) => (
                <tr
                  key={idx}
                  draggable="true"
                  onDragStart={(e) => {
                    dragRef.current = { type: "array", field, fromIndex: idx };
                    e.dataTransfer.effectAllowed = "move";
                    e.dataTransfer.setData("text/plain", JSON.stringify(dragRef.current));
                    e.currentTarget.style.opacity = "0.4";
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    e.currentTarget.style.outline = "2px dashed #3b82f6";
                    e.currentTarget.style.outlineOffset = "-2px";
                  }}
                  onDragLeave={(e) => { e.currentTarget.style.outline = ""; }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.currentTarget.style.opacity = "";
                    e.currentTarget.style.outline = "";
                    if (!dragRef.current || dragRef.current.type !== "array") { dragRef.current = null; return; }
                    const { field: fromField, fromIndex } = dragRef.current;
                    if (fromField !== field || fromIndex === idx) { dragRef.current = null; return; }
                    setContent((prev) => {
                      const updated = { ...prev };
                      const arr = [...(updated[field] || [])];
                      const [moved] = arr.splice(fromIndex, 1);
                      arr.splice(idx, 0, moved);
                      updated[field] = arr;
                      return updated;
                    });
                    dragRef.current = null;
                  }}
                  onDragEnd={(e) => {
                    e.currentTarget.style.opacity = "";
                    e.currentTarget.style.outline = "";
                    dragRef.current = null;
                  }}
                >
                  <td>
                    <div className="AdminOrderBtns">
                      <button className="AdminMoveBtn" onClick={() => handleMoveRowUp(field, idx)} disabled={idx === 0} title="Move up">&#8593;</button>
                      <button className="AdminMoveBtn" onClick={() => handleMoveRowDown(field, idx)} disabled={idx === items.length - 1} title="Move down">&#8595;</button>
                      {useModal && (
                        <button
                          className="AdminEditBtn"
                          onClick={() => handleOpenEditJob(idx)}
                          title="Edit row"
                        >
                          &#9998;
                        </button>
                      )}
                    </div>
                  </td>
                  {columns.map((col) => (
                    <td key={col.key}>
                      {useModal ? (
                        <span className="AdminTableCellText">{displayValue(item, col)}</span>
                      ) : col.type === "textarea" ? (
                        <textarea
                          className="AdminInput AdminTableInput AdminScheduleMinH60"
                          value={item[col.key] || ""}
                          onChange={(e) => handleArrayChange(field, idx, col.key, e.target.value)}
                          placeholder={col.label}
                        />
                      ) : col.type === "icon" ? (
                        <div className="AdminIconCell">
                          <span className="AdminIconPreview">
                            {resolveIcon(item[col.key])}
                          </span>
                          <input
                            className="AdminInput AdminTableInput"
                            value={item[col.kindKey || col.key] || ""}
                            onChange={(e) => handleArrayChange(field, idx, col.kindKey || col.key, e.target.value)}
                            placeholder={col.label}
                          />
                          <select
                            className="AdminInput AdminTableInput AdminScheduleMinW90MaxW120"
                            value=""
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v) handleIconPresetSelect(field, idx, col.key, col.kindKey, v);
                            }}
                          >
                            <option value="">Presets...</option>
                            {VISA_CAREER_ICON_PRESETS.map((p) => (
                              <option key={p} value={p}>{p}</option>
                            ))}
                          </select>
                          <button
                            className="AdminIconUploadBtn"
                            title="Upload icon image"
                            onClick={() => handleIconUploadClick(field, idx, col.key)}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z"/>
                            </svg>
                          </button>
                          {item[col.key] && (
                            <button
                              className="AdminIconBtn AdminIconBtnRed AdminScheduleClearBtn"
                              title="Clear"
                              onClick={() => {
                                setContent((prev) => {
                                  const updated = { ...prev };
                                  const arr = [...(updated[field] || [])];
                                  arr[idx] = { ...arr[idx], [col.key]: "" };
                                  if (col.kindKey) arr[idx][col.kindKey] = "";
                                  updated[field] = arr;
                                  return updated;
                                });
                              }}
                            >
                              &#10005;
                            </button>
                          )}
                        </div>
                      ) : col.type === "select" ? (
                        <select
                          className="AdminInput AdminTableInput"
                          value={item[col.key] || ""}
                          onChange={(e) => handleArrayChange(field, idx, col.key, e.target.value)}
                        >
                          {(col.options || []).map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      ) : col.readonly ? (
                        <span className="AdminTableCellText AdminScheduleText94A3B8">{item[col.key] || "—"}</span>
                      ) : (
                        <input
                          className="AdminInput AdminTableInput"
                          value={item[col.key] || ""}
                          onChange={(e) => handleArrayChange(field, idx, col.key, e.target.value)}
                          placeholder={col.label}
                        />
                      )}
                    </td>
                  ))}
                  <td>
                    <div className="AdminActionBtns">
                      <button className="AdminIconBtn AdminIconBtnBlue" onClick={() => handleAddRowBefore(field, idx)} title="Add row before">&#9650;+</button>
                      <button className="AdminIconBtn AdminIconBtnBlue" onClick={() => handleAddRowAfter(field, idx)} title="Add row after">&#9660;+</button>
                      <button className="AdminIconBtn AdminIconBtnRed" onClick={() => handleDeleteRow(field, idx)} title="Delete row">&#10005;</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWithScrollButtons>
        {items.length === 0 && (
          <div className="AdminEmptyState">{VISA_CAREER_MESSAGES.emptyArray}</div>
        )}
        {items.length > 0 && arraySearch.trim() && !items.some((item) =>
          columns.some((col) =>
            String(item[col.key] || "").toLowerCase().includes(arraySearch.trim().toLowerCase())
          )
        ) && (
          <div className="AdminEmptyState">No results found for "{arraySearch}"</div>
        )}
      </div>
    );
  };

  // ── Render job listings grouped by department as cards ──
  const renderJobListingsCards = () => {
    const departments = (content?.departments || []).filter((d) => String(d.id).trim() !== "all");
    const listings = content?.jobListings || [];

    // Sort
    const sortedListings = [...listings]
      .map((item, idx) => ({ item, idx }))
      .sort((a, b) => {
        const dateA = new Date(a.item.postedDate || 0);
        const dateB = new Date(b.item.postedDate || 0);
        if (jobSortOrder === "newest") return dateB - dateA;
        return dateA - dateB;
      });

    // Filter by search
    const filteredListings = (arraySearch.trim()
      ? sortedListings.filter(({ item }) =>
          VISA_CAREER_COLUMNS.jobListings.some((col) =>
            String(item[col.key] || "").toLowerCase().includes(arraySearch.trim().toLowerCase())
          )
        )
      : sortedListings
    );

    if (departments.length === 0) {
      return (
        <div className="AdminEmptyState">
          Add departments in the Departments tab to organize job listings into cards.
        </div>
      );
    }

    return (
      <div className="AdminScheduleFlexColumnGap20">
        {departments.map((dept) => {
          const deptListings = filteredListings.filter(({ item }) => item.department === dept.id);

          return (
            <div key={dept.id} className="AdminCardMb AdminScheduleCardNoPad">
              {/* Department header */}
              <div className="AdminScheduleDeptHeader">
                <div className="AdminScheduleFlexRowGap10">
                  <h4 className="AdminScheduleDeptTitle">
                    {dept.label || dept.id}
                  </h4>
                  <span className="AdminScheduleDeptBadge">
                    {deptListings.length}
                  </span>
                </div>
              </div>

              {/* Job items */}
              {deptListings.length === 0 ? (
                <div className="AdminScheduleNoJobsMsg">
                  No jobs in this department yet.
                </div>
              ) : (
                <div>
                  {deptListings.map(({ item, idx }) => (
                    <div key={item.id || idx} className="AdminScheduleJobRow">
                      <div className="AdminScheduleFlex1MinW0">
                        <div className="AdminScheduleJobTitle">
                          {item.title || 'Untitled'}
                        </div>
                        <div className="AdminScheduleJobMeta">
                          <span className="AdminScheduleJobId">{item.id || '(new)'}</span>
                          {item.departmentLabel && <span>· {item.departmentLabel}</span>}
                          {item.location && <span>· {item.location}</span>}
                          {item.type && <span>· {item.type}</span>}
                          {item.experience && <span>· {item.experience}</span>}
                          {item.postedDate && <span>· {formatDateOnly(item.postedDate)}</span>}
                        </div>
                      </div>
                      <div className="AdminScheduleJobActions">
                        <button
                          className="AdminEditBtn"
                          onClick={() => handleOpenEditJob(idx)}
                          title="Edit"
                        >
                          &#9998;
                        </button>
                        <button
                          className="AdminIconBtn AdminIconBtnRed AdminText14"
                          onClick={() => handleDeleteRow("jobListings", idx)}
                          title="Delete"
                        >
                          &#10005;
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add Job button per department */}
              <div className="AdminScheduleAddJobSection">
                <button
                  className="AdminButton AdminButtonSmall AdminButtonSecondary AdminText12"
                  onClick={() => handleOpenAddJob(dept.id)}
                >
                  + Add Job to {dept.label || dept.id}
                </button>
              </div>
            </div>
          );
        })}

        {/* Orphaned listings — department no longer exists */}
        {(() => {
          const deptIds = new Set(departments.map((d) => d.id));
          const orphaned = filteredListings.filter(({ item }) => !deptIds.has(item.department));
          if (orphaned.length === 0) return null;
          return (
            <div key="__orphaned__" className="AdminCardMb AdminScheduleCardOrphaned">
              <div className="AdminScheduleDeptOrphanedHeader">
                <div className="AdminScheduleFlexRowGap10">
                  <h4 className="AdminScheduleDeptOrphanedTitle">Uncategorized</h4>
                  <span className="AdminScheduleDeptOrphanedBadge">
                    {orphaned.length}
                  </span>
                </div>
              </div>
              <div>
                {orphaned.map(({ item, idx }) => (
                  <div key={item.id || idx} className="AdminScheduleJobRow">
                    <div className="AdminScheduleFlex1MinW0">
                      <div className="AdminScheduleJobTitle">
                        {item.title || 'Untitled'}
                      </div>
                      <div className="AdminScheduleJobMeta">
                        <span className="AdminScheduleJobId">{item.id || '(new)'}</span>
                        {item.location && <span>· {item.location}</span>}
                        {item.type && <span>· {item.type}</span>}
                        {item.experience && <span>· {item.experience}</span>}
                        {item.postedDate && <span>· {formatDateOnly(item.postedDate)}</span>}
                      </div>
                    </div>
                    <div className="AdminScheduleJobActions">
                      <button className="AdminEditBtn" onClick={() => handleOpenEditJob(idx)} title="Edit">&#9998;</button>
                      <button className="AdminIconBtn AdminIconBtnRed AdminText14" onClick={() => handleDeleteRow("jobListings", idx)} title="Delete">&#10005;</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {arraySearch.trim() && listings.length > 0 && filteredListings.length < listings.length && (
          <div className="AdminEmptyState AdminSchedulePad16">
            Some jobs are hidden by search. Clear search to see all.
          </div>
        )}
      </div>
    );
  };

  const renderEmailConfig = () => {
    const cfg = content?.emailConfig || {};
    const setCfg = (key, val) => setContent((prev) => ({
      ...prev,
      emailConfig: { ...(prev.emailConfig || {}), [key]: val },
    }));

    const textarea = (key, placeholder, variables) => (
      <textarea
        className="AdminInput AdminScheduleMinH200"
        placeholder={placeholder}
        value={cfg[key] || ""}
        onChange={(e) => setCfg(key, e.target.value)}
      />
    );

    const input = (key, placeholder) => (
      <input
        className="AdminInput"
        type="text"
        placeholder={placeholder}
        value={cfg[key] || ""}
        onChange={(e) => setCfg(key, e.target.value)}
      />
    );

    const varTags = (vars) => (
      <p className="AdminText12 AdminTextMuted AdminScheduleMt2">
        {vars.map((v) => <code key={v} style={{ marginRight: 6 }}>{`{{ ${v} }}`}</code>)}
      </p>
    );

    const emailCard = (title, desc, fields) => (
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 16 }}>
        <h5 style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 600, color: "#1B2A4A" }}>{title}</h5>
        {desc && <p className="AdminText12 AdminTextMuted" style={{ marginBottom: 12 }}>{desc}</p>}
        {fields}
      </div>
    );

    return (
      <div className="AdminCardMb">
        <div className="AdminSectionHeader">
          <h4 className="AdminSectionTitle">Email Configuration</h4>
        </div>
        <div className="AdminScheduleEmailConfigSection">
          <div className="AdminScheduleInfoBox">
            <strong>From Email &amp; From Name</strong> are configured globally in{" "}
            <a href="/admin/email-settings" className="AdminScheduleInfoBoxLink">Email Settings</a>.
            Changes there apply everywhere.
          </div>

          {/* ── New Application section ── */}
          <div style={{ marginTop: 8 }}>
            <h4 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 700, color: "#1B2A4A" }}>New Application</h4>
            <div className="AdminScheduleGrid2ColGap24">
              {emailCard("Customer Confirmation Email", "Sent to the applicant when they apply.", (
                <>
                  <label className="AdminLabel AdminMb4">Subject</label>
                  {input("jobSubject", "Application Received — TheVisaCo Careers")}
                  {varTags(["applicantName", "applicantEmail", "jobTitle"])}
                  <label className="AdminLabel AdminMb4" style={{ marginTop: 8 }}>Body / Draft</label>
                  {textarea("jobBody", "Hi {{ applicantName }},...")}
                  {varTags(["applicantName", "applicantEmail", "jobTitle"])}
                </>
              ))}
              {emailCard("Admin Notification", "Sent to the admin when someone applies.", (
                <>
                  <label className="AdminLabel AdminMb4">Subject</label>
                  {input("adminJobSubject", "New Job Application — {{ jobTitle }}")}
                  {varTags(["jobTitle", "applicantName"])}
                  <label className="AdminLabel AdminMb4" style={{ marginTop: 8 }}>Body / Draft</label>
                  {textarea("adminJobBody", "New application for {{ jobTitle }} from {{ applicantName }}...")}
                  {varTags(["applicantName", "applicantEmail", "jobTitle", "phone", "experience", "linkedIn", "coverNote", "resumeUrl", "resumeName", "adminUrl"])}
                </>
              ))}
            </div>
          </div>

          {/* ── Acceptance section ── */}
          <div style={{ marginTop: 16 }}>
            <h4 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 700, color: "#166534" }}>Acceptance</h4>
            <div className="AdminScheduleGrid2ColGap24">
              {emailCard("Acceptance Email (to Candidate)", "Sent when an application is accepted.", (
                <>
                  <label className="AdminLabel AdminMb4">Subject</label>
                  {input("acceptanceSubject", "You're Accepted — TheVisaCo")}
                  {varTags(["applicantName", "jobTitle"])}
                  <label className="AdminLabel AdminMb4" style={{ marginTop: 8 }}>Body / Draft</label>
                  {textarea("acceptanceBody", "Hi {{ applicantName }},\n\nCongratulations...")}
                  {varTags(["applicantName", "applicantEmail", "jobTitle"])}
                </>
              ))}
              {emailCard("Admin Notification (Acceptance)", "Sent to the admin when an application is accepted.", (
                <>
                  <label className="AdminLabel AdminMb4">Subject</label>
                  {input("adminAcceptanceSubject", "Application Accepted — {{ jobTitle }}")}
                  {varTags(["jobTitle", "applicantName"])}
                  <label className="AdminLabel AdminMb4" style={{ marginTop: 8 }}>Body / Draft</label>
                  {textarea("adminAcceptanceBody", "Application for {{ jobTitle }} from {{ applicantName }} has been accepted...")}
                  {varTags(["applicantName", "applicantEmail", "jobTitle", "phone", "adminUrl"])}
                </>
              ))}
            </div>
          </div>

          {/* ── Rejection section ── */}
          <div style={{ marginTop: 16 }}>
            <h4 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 700, color: "#991b1b" }}>Rejection</h4>
            <div className="AdminScheduleGrid2ColGap24">
              {emailCard("Rejection Email (to Candidate)", "Sent when an application is rejected.", (
                <>
                  <label className="AdminLabel AdminMb4">Subject</label>
                  {input("rejectionSubject", "Update on Your Application — TheVisaCo")}
                  {varTags(["applicantName", "jobTitle"])}
                  <label className="AdminLabel AdminMb4" style={{ marginTop: 8 }}>Body / Draft</label>
                  {textarea("rejectionBody", "Hi {{ applicantName }},...")}
                  {varTags(["applicantName", "applicantEmail", "jobTitle"])}
                </>
              ))}
              {emailCard("Admin Notification (Rejection)", "Sent to the admin when an application is rejected.", (
                <>
                  <label className="AdminLabel AdminMb4">Subject</label>
                  {input("adminRejectionSubject", "Application Rejected — {{ jobTitle }}")}
                  {varTags(["jobTitle", "applicantName"])}
                  <label className="AdminLabel AdminMb4" style={{ marginTop: 8 }}>Body / Draft</label>
                  {textarea("adminRejectionBody", "Application for {{ jobTitle }} from {{ applicantName }} has been rejected...")}
                  {varTags(["applicantName", "applicantEmail", "jobTitle", "phone", "adminUrl"])}
                </>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSimpleListEditor = (field, selectOptions) => {
    const items = content?.[field] || [];
    return (
      <div className="AdminCardMb">
        <div className="AdminSectionHeader">
          <h4 className="AdminSectionTitle">
            {field.replace(/([A-Z])/g, " $1").trim()}
          </h4>
          <div className="AdminFlexRowGap8 AdminFlexShrink0">
            <div className={`AdminPillSearchWrap ${searchOpen ? 'is-open' : ''}`}>
              <span className="AdminPillSearchIcon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
              </span>
              <input
                ref={searchRef}
                type="text"
                className="AdminPillSearch"
                placeholder="Search..."
                value={arraySearch}
                onChange={(e) => setArraySearch(e.target.value)}
                onBlur={() => setTimeout(() => setSearchOpen(false), 200)}
              />
              {!searchOpen && (
                <button className="AdminPillSearchOverlay" onClick={() => setSearchOpen(true)} type="button" aria-label="Search" />
              )}
            </div>
            <button
              className="AdminButton AdminButtonSmall AdminButtonSecondary AdminAddRowBtn"
              onClick={() => {
                setContent((prev) => {
                  const updated = { ...prev };
                  updated[field] = [...(updated[field] || []), ""];
                  return updated;
                });
              }}
            >
              + Add Item
            </button>
          </div>
        </div>          <TableWithScrollButtons>
          <table className="AdminTable AdminTableEditor">
                <thead>
                  <tr>
                    <th className="AdminThOrder">Order</th>
                    <th>Value</th>
                    <th className="AdminThActions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items
                    .map((item, idx) => ({ item, idx }))
                    .filter(({ item }) => {
                      if (!arraySearch.trim()) return true;
                      return String(typeof item === "string" ? item : item?.[0] || "").toLowerCase().includes(arraySearch.trim().toLowerCase());
                    })
                    .map(({ item, idx }) => (
                    <tr key={idx}
                      draggable="true"
                      onDragStart={(e) => {
                        dragRef.current = { type: "array", field, fromIndex: idx };
                        e.dataTransfer.effectAllowed = "move";
                        e.dataTransfer.setData("text/plain", JSON.stringify(dragRef.current));
                        e.currentTarget.style.opacity = "0.4";
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "move";
                        e.currentTarget.style.outline = "2px dashed #3b82f6";
                        e.currentTarget.style.outlineOffset = "-2px";
                      }}
                      onDragLeave={(e) => { e.currentTarget.style.outline = ""; }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.currentTarget.style.opacity = "";
                        e.currentTarget.style.outline = "";
                        if (!dragRef.current || dragRef.current.type !== "array") { dragRef.current = null; return; }
                        const { field: fromField, fromIndex } = dragRef.current;
                        if (fromField !== field || fromIndex === idx) { dragRef.current = null; return; }
                        setContent((prev) => {
                          const updated = { ...prev };
                          const arr = [...(updated[field] || [])];
                          const [moved] = arr.splice(fromIndex, 1);
                          arr.splice(idx, 0, moved);
                          updated[field] = arr;
                          return updated;
                        });
                        dragRef.current = null;
                      }}
                      onDragEnd={(e) => {
                        e.currentTarget.style.opacity = "";
                        e.currentTarget.style.outline = "";
                        dragRef.current = null;
                      }}
                    >
                      <td>
                        <div className="AdminOrderBtns">
                          <button className="AdminMoveBtn" onClick={() => {
                            if (idx === 0) return;
                            setContent((prev) => {
                              const updated = { ...prev };
                              const arr = [...(updated[field] || [])];
                              [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
                              updated[field] = arr;
                              return updated;
                            });
                          }} disabled={idx === 0} title="Move up">&#8593;</button>
                          <button className="AdminMoveBtn" onClick={() => {
                            setContent((prev) => {
                              const updated = { ...prev };
                              const arr = [...(updated[field] || [])];
                              if (idx >= arr.length - 1) return prev;
                              [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
                              updated[field] = arr;
                              return updated;
                            });
                          }} disabled={idx === items.length - 1} title="Move down">&#8595;</button>
                        </div>
                      </td>
                      <td>
                        {selectOptions ? (
                          <select
                            className="AdminInput AdminTableInput"
                            value={typeof item === "string" ? item : ""}
                            onChange={(e) => {
                              setContent((prev) => {
                                const updated = { ...prev };
                                const arr = [...(updated[field] || [])];
                                arr[idx] = e.target.value;
                                updated[field] = arr;
                                return updated;
                              });
                            }}
                          >
                            <option value="">Select a label...</option>
                            {selectOptions.map((opt) => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        ) : (
                          <input
                            className="AdminInput AdminTableInput"
                            value={typeof item === "string" ? item : ""}
                            onChange={(e) => {
                              setContent((prev) => {
                                const updated = { ...prev };
                                const arr = [...(updated[field] || [])];
                                arr[idx] = e.target.value;
                                updated[field] = arr;
                                return updated;
                              });
                            }}
                            placeholder="Enter value..."
                          />
                        )}
                      </td>
                      <td>
                        <div className="AdminActionBtns">
                          <button className="AdminIconBtn AdminIconBtnBlue" onClick={() => {
                            setContent((prev) => {
                              const updated = { ...prev };
                              const arr = [...(updated[field] || [])];
                              arr.splice(idx, 0, "");
                              updated[field] = arr;
                              return updated;
                            });
                          }} title="Add row before">&#9650;+</button>
                          <button className="AdminIconBtn AdminIconBtnBlue" onClick={() => {
                            setContent((prev) => {
                              const updated = { ...prev };
                              const arr = [...(updated[field] || [])];
                              arr.splice(idx + 1, 0, "");
                              updated[field] = arr;
                              return updated;
                            });
                          }} title="Add row after">&#9660;+</button>
                          <button className="AdminIconBtn AdminIconBtnRed" onClick={() => {
                            setContent((prev) => {
                              const updated = { ...prev };
                              updated[field] = (updated[field] || []).filter((_, i) => i !== idx);
                              return updated;
                            });
                          }} title="Delete row">&#10005;</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableWithScrollButtons>
        {items.length === 0 && !arraySearch.trim() && (
          <div className="AdminEmptyState">No items. Click "Add Item" to create one.</div>
        )}
        {arraySearch.trim() && items.filter((i) => String(typeof i === "string" ? i : i?.[0] || "").toLowerCase().includes(arraySearch.trim().toLowerCase())).length === 0 && items.length > 0 && (
          <div className="AdminEmptyState">No items found for "{arraySearch}"</div>
        )}
      </div>
    );
  };

  const renderFilterEditor = () => {
    const filterFields = [
      { field: "locationOptions", label: "Location (Country) Options", columns: VISA_CAREER_COLUMNS.locationOptions },
      { field: "jobTypeOptions", label: "Job Type Options", columns: VISA_CAREER_COLUMNS.jobTypeOptions },
      { field: "sortOptions", label: "Sort Options", columns: VISA_CAREER_COLUMNS.sortOptions },
    ];
    const hasAll = (field) => ["locationOptions", "jobTypeOptions"].includes(field);
    return (
      <div className="AdminCardMb">
        {filterFields.map(({ field, label, columns }) => {
          const items = content?.[field] || [];
          const filteredItems = hasAll(field) ? items.filter((item) => item.value !== "") : items;
          return (
          <div key={field} className="AdminScheduleMb24">
            <div className="AdminSectionHeader">
              <h4 className="AdminSectionTitle">{label}</h4>
              <div className="AdminFlexRowGap8 AdminFlexShrink0">
                <div className={`AdminPillSearchWrap ${filterSearchOpen ? 'is-open' : ''}`}>
                  <span className="AdminPillSearchIcon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    </svg>
                  </span>
                  <input
                    ref={filterSearchRef}
                    type="text"
                    className="AdminPillSearch"
                    placeholder="Search..."
                    value={filterSearch}
                    onChange={(e) => setFilterSearch(e.target.value)}
                    onBlur={() => setTimeout(() => setFilterSearchOpen(false), 200)}
                  />
                  {!filterSearchOpen && (
                    <button className="AdminPillSearchOverlay" onClick={() => setFilterSearchOpen(true)} type="button" aria-label="Search" />
                  )}
                </div>
                <button
                  className="AdminButton AdminButtonSmall AdminButtonSecondary AdminAddRowBtn"
                  onClick={() => handleAddRowAfter(field, (content?.[field] || []).length - 1)}
                >
                  + Add Row
                </button>
              </div>
            </div>
            {hasAll(field) && (
              <div className="AdminScheduleFilterHintRow">
                <span className="AdminScheduleFilterHintLabel">
                  {field === "locationOptions" ? "All Locations" : "All Types"}
                </span>
                <span className="AdminScheduleFilterHintDesc">(always present — clears filter)</span>
              </div>
            )}
            <TableWithScrollButtons>
          <table className="AdminTable AdminTableEditor">
                <thead>
                  <tr>
                    <th className="AdminThOrder">Order</th>
                    {columns.map((col) => (
                      <th key={col.key}>{col.label}</th>
                    ))}
                    <th className="AdminThActions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems
                    .map((item) => {
                    const realIdx = items.findIndex((x) => x === item);
                    return { item, realIdx };
                    })
                    .filter(({ item }) => {
                      if (!filterSearch.trim()) return true;
                      const q = filterSearch.trim().toLowerCase();
                      return columns.some((col) => String(item[col.key] || "").toLowerCase().includes(q));
                    })
                    .map(({ item, realIdx }) => {
                    return (
                    <tr key={realIdx}>
                      <td>
                        <div className="AdminOrderBtns">
                          <button className="AdminMoveBtn" onClick={() => handleMoveRowUp(field, realIdx)} disabled={realIdx === 0} title="Move up">&#8593;</button>
                          <button className="AdminMoveBtn" onClick={() => handleMoveRowDown(field, realIdx)} disabled={realIdx === items.length - 1} title="Move down">&#8595;</button>
                        </div>
                      </td>
                      {columns.map((col) => (
                        <td key={col.key}>
                          <input
                            className="AdminInput AdminTableInput"
                            value={item[col.key] || ""}
                            onChange={(e) => handleArrayChange(field, realIdx, col.key, e.target.value)}
                            placeholder={col.label}
                          />
                        </td>
                      ))}
                      <td>
                        <div className="AdminActionBtns">
                          <button className="AdminIconBtn AdminIconBtnBlue" onClick={() => handleAddRowBefore(field, realIdx)} title="Add row before">&#9650;+</button>
                          <button className="AdminIconBtn AdminIconBtnBlue" onClick={() => handleAddRowAfter(field, realIdx)} title="Add row after">&#9660;+</button>
                          <button className="AdminIconBtn AdminIconBtnRed" onClick={() => handleDeleteRow(field, realIdx)} title="Delete row">&#10005;</button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </TableWithScrollButtons>
            {filteredItems.length === 0 && !hasAll(field) && (
              <div className="AdminEmptyState">{VISA_CAREER_MESSAGES.emptyArray}</div>
            )}
            {filterSearch.trim() && filteredItems.length > 0 && filteredItems.filter((item) => columns.some((col) => String(item[col.key] || "").toLowerCase().includes(filterSearch.trim().toLowerCase()))).length === 0 && (
              <div className="AdminEmptyState">No items found for "{filterSearch}"</div>
            )}
          </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return <div className="AdminEmptyState">{VISA_CAREER_MESSAGES.loading}</div>;
  }

  if (!content) {
    return (
      <div>
        <button className="AdminButton AdminButtonSmall AdminButtonSecondary AdminBackBtn" onClick={onBack}>
          &larr; Back to Content Manager
        </button>
        <div className="AdminEmptyState">{VISA_CAREER_MESSAGES.noContent}</div>
      </div>
    );
  }

  const isDirty = JSON.stringify(content) !== snapshotRef.current;

  return (
    <div className="AdminCard">
      <div className="AdminCardHeader">
        <h3 className="AdminFooterH3">
          Edit Visa Career Content
          {activeTab !== "data" && Array.isArray(content?.[activeTab]) && (
            <span className="AdminCountBadge">{content[activeTab].length}</span>
          )}
        </h3>
        <div className="AdminHeaderActionsRow">
          <button className="AdminButton AdminButtonSmall AdminSubmitBtn AdminNowrap" onClick={handleSubmit} disabled={!isDirty || saving}>
            {saving ? "Publishing..." : getSubmitButtonLabel(user?.role)}
          </button>
          <button className="AdminButton AdminButtonSmall AdminSheetBtn" onClick={handleOpenSheet}>
            Google Sheet
          </button>
          <button className="AdminSheetIconBtn" onClick={handlePushToSheet} disabled={loading} title="Push to Sheet">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
          </button>
          <button className="AdminSheetIconBtn" onClick={handlePullFromSheet} disabled={loading} title="Pull from Sheet">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
          </button>
        </div>
      </div>

      <input type="file" accept="image/png,image/gif,image/jpeg,image/webp" ref={fileInputRef} className="AdminHidden" onChange={handleIconFileChange} />

      {message && (
        <p className={`AdminMsgBox ${message.includes("success") ? "AdminSuccess" : "AdminError"}`}>
          {message}
        </p>
      )}

      <div className="AdminFlexRowGap8" style={{ marginBottom: 12, alignItems: "center" }}>
        <button className="AdminTabHamburger" onClick={() => setShowMobileMenu(true)} title="Menu">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="3" y1="6" x2="21" y2="6"/>
            <line x1="3" y1="12" x2="21" y2="12"/>
            <line x1="3" y1="18" x2="21" y2="18"/>
          </svg>
        </button>
        <div
          className="AdminTabBar"
          ref={tabBarRef}
          style={{ display: "flex", gap: 0, borderBottom: "2px solid #e2e8f0", marginBottom: 0 }}
        >
          {VISA_CAREER_TABS.map((tab) => (
            <button
              key={tab.id}
              draggable
              onDragStart={(e) => { tabDragRef.current = tab.id; e.target.style.opacity = '0.5'; }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const fromId = tabDragRef.current;
                if (!fromId || fromId === tab.id) return;
                setActiveTab(tab.id);
              }}
              onDragEnd={(e) => { e.target.style.opacity = '1'; tabDragRef.current = null; }}
              className={`AdminTabBtn ${activeTab === tab.id ? "AdminTabBtnActive" : ""}`}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: "10px 20px", border: "none", background: activeTab === tab.id ? "#1B2A4A" : "transparent",
                color: activeTab === tab.id ? "#fff" : "#64748b", cursor: "grab", fontSize: 14, fontWeight: 600,
                borderRadius: "8px 8px 0 0", transition: "all 0.2s",
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {showMobileMenu && (
        <>
          <div className="AdminBottomSheetOverlay" onClick={() => setShowMobileMenu(false)} />
          <div className="AdminBottomSheet">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <strong style={{ fontSize: 16, color: "#1B2A4A" }}>Tabs</strong>
              <button className="AdminCloseBtn" onClick={() => setShowMobileMenu(false)}>&times;</button>
            </div>
            <div
              className="AdminTabBar"
              style={{ display: "flex", gap: 0, borderBottom: "2px solid #e2e8f0" }}
            >
              {VISA_CAREER_TABS.map((tab) => (
                <button
                  key={tab.id}
                  draggable
                  onDragStart={(e) => { tabDragRef.current = tab.id; e.target.style.opacity = '0.5'; }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const fromId = tabDragRef.current;
                    if (!fromId || fromId === tab.id) return;
                    setActiveTab(tab.id); setShowMobileMenu(false);
                  }}
                  onDragEnd={(e) => { e.target.style.opacity = '1'; tabDragRef.current = null; }}
                  className={`AdminTabBtn ${activeTab === tab.id ? "AdminTabBtnActive" : ""}`}
                  onClick={() => { setActiveTab(tab.id); setShowMobileMenu(false); }}
                  style={{
                    padding: "10px 20px", border: "none", background: activeTab === tab.id ? "#1B2A4A" : "transparent",
                    color: activeTab === tab.id ? "#fff" : "#64748b", cursor: "grab", fontSize: 14, fontWeight: 600,
                    borderRadius: "8px 8px 0 0", transition: "all 0.2s",
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {activeTab === "data" && (
        <div className="AdminCard">
          <div className="AdminDataHeader">
            <h4 className="AdminSectionTitle">Data</h4>
          </div>
          <div className="AdminDataInfo">
            <div>
              <span className="AdminDataLabel">Last Updated</span>
              <p className="AdminDataValue">
                {sectionInfo?.updatedAt ? new Date(sectionInfo.updatedAt).toLocaleString() : "Not published"}
              </p>
            </div>
            <div>
              <span className="AdminDataLabel">Section Key</span>
              <p className="AdminDataValue">{section?.key || "visaCareer"}</p>
            </div>
          </div>
          <textarea
            className="AdminJsonEditor"
            value={JSON.stringify(content, null, 2)}
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value);
                setContent(parsed);
                setMessage("");
              } catch {
                setMessage(VISA_CAREER_MESSAGES.invalidJson);
              }
            }}
          />
        </div>
      )}

      {activeTab === "departments" && renderArrayEditor("departments", VISA_CAREER_COLUMNS.departments)}
      {activeTab === "culture" && renderArrayEditor("culture", VISA_CAREER_COLUMNS.culture)}
      {activeTab === "howHiringWorks" && renderArrayEditor("howHiringWorks", VISA_CAREER_COLUMNS.howHiringWorks)}
      {activeTab === "whyVisaCo" && renderSimpleListEditor("whyVisaCo")}
      {activeTab === "roleOverviewLabels" && renderSimpleListEditor("roleOverviewLabels", ROLE_OVERVIEW_LABEL_OPTIONS)}
      {activeTab === "jobListings" && (
        <div className="AdminCardMb">
          <div className="AdminSectionHeader">
            <h4 className="AdminSectionTitle">Job Listings</h4>
            <div className="AdminFlexRowGap8 AdminFlexShrink0">
              <div className={`AdminPillSearchWrap ${searchOpen ? 'is-open' : ''}`}>
                <span className="AdminPillSearchIcon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                </span>
                <input
                  ref={searchRef}
                  type="text"
                  className="AdminPillSearch"
                  placeholder="Search..."
                  value={arraySearch}
                  onChange={(e) => setArraySearch(e.target.value)}
                  onBlur={() => setTimeout(() => setSearchOpen(false), 200)}
                />
                {!searchOpen && (
                  <button className="AdminPillSearchOverlay" onClick={() => setSearchOpen(true)} type="button" aria-label="Search" />
                )}
              </div>
              <button
                className={`AdminButton AdminButtonSmall ${jobSortOrder === "newest" ? "" : "AdminButtonSecondary"} AdminTabBtn`}
                onClick={() => setJobSortOrder("newest")}
              >
                Newest
              </button>
              <button
                className={`AdminButton AdminButtonSmall ${jobSortOrder === "oldest" ? "" : "AdminButtonSecondary"} AdminTabBtn`}
                onClick={() => setJobSortOrder("oldest")}
              >
                Oldest
              </button>
            </div>
          </div>
          {renderJobListingsCards()}
        </div>
      )}
      {activeTab === "benefits" && renderArrayEditor("benefits", VISA_CAREER_COLUMNS.benefits)}
      {activeTab === "filter" && renderFilterEditor()}
      {activeTab === "formFields" && renderArrayEditor("formFields", VISA_CAREER_COLUMNS.formFields)}

      {activeTab === "countryCodes" && renderArrayEditor("countryCodes", VISA_CAREER_COLUMNS.countryCodes)}

      {activeTab === "emailSettings" && renderEmailConfig()}

      {showNoteModal && (
        <div className="AdminModal">
          <div className="AdminModalContent AdminScheduleMaxW440">
            <h3 className="AdminModalTitle">Note for Approval</h3>
            <p className="AdminText13 AdminTextMuted AdminM0B12">
              {user?.role === "Staff"
                ? "Your changes will be sent to the Manager for approval."
                : "Your changes will be sent to the Admin for approval."}
            </p>
            <textarea
              className="AdminNoteTextarea"
              placeholder="Describe what you changed and why..."
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
            />
            <div className="AdminNoteModalRow">
              <button className="AdminButton AdminButtonSmall AdminButtonSecondary" onClick={() => setShowNoteModal(false)}>Cancel</button>
              <button className="AdminButton AdminButtonSmall" onClick={handleConfirmSubmit} disabled={!noteText.trim()}>Submit</button>
            </div>
          </div>
        </div>
      )}

      {showDuplicateModal && (
        <div className="AdminModal">
          <div className="AdminModalContent AdminModalMaxW480">
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <h3 className="AdminModalTitle" style={{margin:0}}>Duplicate Rows Found</h3>
              <button className="AdminIconBtn" onClick={() => setShowDuplicateModal(false)} title="Close" style={{fontSize:28,width:36,height:36,flexShrink:0}}>&times;</button>
            </div>
            <p className="AdminText13 AdminTextMuted AdminM0B12">
              {duplicateInfo.length} duplicate row(s) detected. You can remove them or continue anyway.
            </p>
            {duplicateInfo.map((d, i) => (
              <div key={i} className="AdminDupWarningBox">
                <div className="AdminDupItem">
                  <strong>{d.tabLabel}</strong>: Row {d.duplicateIndex + 1} matches Row {d.existingIndex + 1}
                </div>
              </div>
            ))}
            <div className="AdminFlexCenterG12 AdminMt16">
              <button className="AdminButton AdminButtonSmall AdminButtonDanger" onClick={handleRemoveDuplicates} disabled={duplicateActionLoading}>
                {duplicateActionLoading ? "Removing..." : "Remove Duplicates"}
              </button>
              <button className="AdminButton AdminButtonSmall" onClick={handleContinueAnyway}>Continue Anyway</button>
            </div>
          </div>
        </div>
      )}

      {showSuccessOverlay && (
        <div className="AdminModal" onClick={() => setShowSuccessOverlay(false)}>
          <div className="AdminModalContent AdminModalMaxW400" style={{ textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: 48, marginBottom: 8 }}>
              {successTitle === "Changes Published" ? "\u2705" : "\uD83D\uDCEB"}
            </div>
            <h3 className="AdminModalTitle" style={{ marginBottom: 4 }}>{successTitle}</h3>
            <p className="AdminText14 AdminTextMuted" style={{ margin: "0 0 20px 0" }}>
              {successTitle === "Changes Published" ? "Changes are now live." : "Your request has been submitted for review."}
            </p>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <button className="AdminButton AdminButtonSmall AdminSubmitBtn" onClick={() => setShowSuccessOverlay(false)}>OK</button>
            </div>
          </div>
        </div>
      )}

      {showJobForm && (
        <div className="AdminModalOverlayFixed">
          <div className="AdminModalContentBox AdminScheduleMaxW640">
            <button
              onClick={handleCloseJobForm}
              className="AdminModalCloseBtn"
              title="Close"
            >
              &#10005;
            </button>
            <h3 className="AdminModalFormTitle">
              {editJobIndex === null ? 'Add' : 'Edit'} Job Listing
            </h3>
            <p className="AdminModalFormSubtitle">
              Fill in the job listing details and press Continue to add it to the table.
            </p>
            <div className="AdminModalFormGrid">
              {VISA_CAREER_COLUMNS.jobListings?.map((col) => (
                <div key={col.key} className={col.type === "textarea" ? "AdminModalFormGridFull" : ""}>
                  <label className="AdminModalFormLabel">{col.label}</label>
                  {col.readonly ? (
                    <div className="AdminScheduleJobIdDisplay">
                      <input
                        className="AdminInput AdminModalFormInput"
                        value={editJobItem[col.key] || ""}
                        readOnly
                        tabIndex={-1}
                        style={{ backgroundColor: "#f1f5f9", color: "#1B2A4A", cursor: "not-allowed", fontWeight: 600 }}
                      />
                    </div>
                  ) : col.type === "textarea" ? (
                    <textarea
                      className="AdminInput AdminModalFormInput AdminScheduleMinH80"
                      value={editJobItem[col.key] || ""}
                      onChange={(e) => handleJobFieldChange(col.key, e.target.value)}
                      placeholder={col.label}
                    />
                  ) : col.type === "date" ? (
                    <input
                      type="date"
                      className="AdminInput AdminModalFormInput"
                      value={editJobItem[col.key] || ""}
                      onChange={(e) => handleJobFieldChange(col.key, e.target.value)}
                    />
                  ) : col.type === "departmentSelect" ? (
                    <select
                      className="AdminInput AdminModalFormInput"
                      value={editJobItem[col.key] || ""}
                      onChange={(e) => handleJobFieldChange(col.key, e.target.value)}
                    >
                      <option value="">Select Department...</option>
                      {(content?.departments || [])
                        .filter((d) => String(d.id).trim() !== "all")
                        .map((dept) => (
                          <option key={dept.id} value={dept.id}>{dept.label || dept.id}</option>
                        ))}
                    </select>
                  ) : col.type === "dynamicSelect" ? (
                    <select
                      className="AdminInput AdminModalFormInput"
                      value={editJobItem[col.key] || ""}
                      onChange={(e) => handleJobFieldChange(col.key, e.target.value)}
                    >
                      <option value="">Select {col.label}...</option>
                      {(content?.[col.key === "location" ? "locationOptions" : "jobTypeOptions"] || []).map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      className="AdminInput AdminModalFormInput"
                      value={editJobItem[col.key] || ""}
                      onChange={(e) => handleJobFieldChange(col.key, e.target.value)}
                      placeholder={col.label}
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="AdminModalBtnRow">
              <button
                className="AdminButton AdminButtonSmall"
                onClick={handleSaveJobForm}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminVisaCareerEditor;
